import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The Files upload queue (TASK-158.5).
//
// Owns everything about putting bytes into Files: the size check, the direct
// upload, the per-file row in the floating panel, and the hidden form whose
// submission is what actually creates the TeamFile. Both entry points feed it
// the same way — the page dropzone dispatches files at it, and so does the Add
// Files modal when more than one file is chosen — so there is one queue and one
// upload path however the files arrived.
//
// Files go up ONE AT A TIME, and each one's row is created by its own POST to
// uploads#create, whose turbo stream prepends it. That is what makes a failure
// local: a file that is refused marks its own row and the queue carries on with
// the next one. Uploading in parallel would be faster and would make a mid-batch
// failure much harder to report honestly.
export default class extends Controller {
  static targets = ["panel", "heading", "rows", "rowTemplate", "form", "blobId", "name"]

  static values = {
    directUploadUrl: String,
    maxSizeMb: { type: Number, default: 50 },
    destination: String
  }

  connect() {
    this.queue = []
    this.running = false
    this.done = 0
    this.total = 0
  }

  // Entry point for both the dropzone and the modal.
  enqueue(event) {
    const files = Array.from(event.detail?.files || [])
    if (files.length === 0) return

    files.forEach((file) => this.add(file))

    // A batch chosen in the Add Files modal has nothing more to do there —
    // the panel takes over from here. Harmless when the modal is not open.
    window.dispatchEvent(new CustomEvent("modal:close-requested"))

    this.panelTarget.classList.remove("hidden")
    this.updateHeading()
    if (!this.running) this.run()
  }

  add(file) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("[data-role='name']").textContent = file.name

    if (file.size > this.maxSizeMbValue * 1024 * 1024) {
      // Never leaves the machine. The server checks the same number, because a
      // client check is a courtesy rather than a rule.
      this.setState(row, "error", `Over the ${this.maxSizeMbValue} MB limit`)
      this.rowsTarget.append(row)
      return
    }

    this.setState(row, "waiting", `${this.humanSize(file.size)} · Waiting`)
    this.rowsTarget.append(row)
    this.total += 1
    this.queue.push({ file, row })
  }

  async run() {
    this.running = true

    while (this.queue.length > 0) {
      const item = this.queue.shift()
      // One file's failure is its own row's business; the rest keep going.
      try {
        await this.upload(item)
        this.done += 1
      } catch (error) {
        this.setState(item.row, "error", "Upload failed")
      }
      this.updateHeading()
    }

    this.running = false
    this.updateHeading()
  }

  upload({ file, row }) {
    return new Promise((resolve, reject) => {
      this.setState(row, "uploading", "")
      const track = row.querySelector("[data-role='track']")
      const bar = row.querySelector("[data-role='bar']")
      const percent = row.querySelector("[data-role='percent']")
      track.classList.remove("hidden")
      percent.classList.remove("hidden")

      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) return

            const value = Math.round((event.loaded / event.total) * 100)
            bar.style.width = `${value}%`
            percent.textContent = `${value}%`
          })
        }
      })

      upload.create((error, blob) => {
        track.classList.add("hidden")
        percent.classList.add("hidden")

        if (error) return reject(error)

        this.createRecord(blob, file, row).then(resolve, reject)
      })
    })
  }

  // Submitting the hidden form rather than fetching puts the response through
  // Turbo, so the row, the storage meter and the toast all come back from
  // uploads/create.turbo_stream.erb — the same template the modal uses.
  // Waiting for submit-end before the next file keeps two submissions of the
  // same form element from overlapping.
  createRecord(blob, file, row) {
    return new Promise((resolve, reject) => {
      this.blobIdTarget.value = blob.signed_id
      this.nameTarget.value = file.name

      const finished = (event) => {
        this.formTarget.removeEventListener("turbo:submit-end", finished)

        if (event.detail?.success) {
          this.setState(row, "done", `${this.humanSize(file.size)} · Uploaded`)
          resolve()
        } else {
          reject(new Error("rejected"))
        }
      }

      this.formTarget.addEventListener("turbo:submit-end", finished)
      this.formTarget.requestSubmit()
    })
  }

  // Only offered on a row that never started: there is nothing to cancel, the
  // button just clears the line so the panel stops reporting a file the member
  // has already dealt with.
  remove(event) {
    event.preventDefault()
    event.target.closest("[data-queue-row]").remove()

    if (this.rowsTarget.children.length === 0) this.dismiss()
  }

  dismiss() {
    this.panelTarget.classList.add("hidden")
    this.rowsTarget.innerHTML = ""
    this.done = 0
    this.total = 0
  }

  updateHeading() {
    const destination = this.destinationValue

    this.headingTarget.textContent = this.running
      ? `Uploading ${Math.min(this.done + 1, this.total)} of ${this.total} to ${destination}`
      : `Uploaded ${this.done} of ${this.total} to ${destination}`
  }

  setState(row, state, meta) {
    row.querySelectorAll("[data-state]").forEach((icon) => {
      icon.classList.toggle("hidden", icon.dataset.state !== state)
    })

    row.querySelector("[data-role='meta']").textContent = meta
    row.querySelector("[data-role='meta']").classList.toggle("text-red-600", state === "error")
    row.querySelector("[data-role='name']").classList.toggle("text-gray-500", state === "waiting")
    row.querySelector("[data-role='remove']").classList.toggle("hidden", state !== "error")
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
